import React from 'react';
import { motion, useReducedMotion, useScroll, useSpring } from 'framer-motion';

/**
 * African textile detailing that never sits behind reading text.
 *
 * The previous version laid a pattern over the whole viewport, which put texture
 * behind every paragraph. This version confines it to two places where there is
 * never any copy:
 *
 * 1. The side gutters outside the 1240px content column, on screens wide enough
 *    to have them (1440px and up). Each gutter carries a woven Kente strip that
 *    drifts slowly, an Adinkrahene mark, and on the right a thread that stitches
 *    itself in as the reader scrolls.
 * 2. A slim woven Kente ribbon (KenteRibbon, below) used as a divider at the top
 *    of the footer, so phones and laptops see the motif too.
 *
 * Both are aria-hidden, take no pointer events, and hold still under reduced motion.
 */

const CONTENT_WIDTH = 1240;
const GUTTER_GAP = 32;

// One 48px repeat of a Kente strip: bold bands, a checker of woven blocks, and fine
// weft lines, in the site accent, white and the raised navy.
const KENTE = encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="96" viewBox="0 0 48 96">
    <rect width="48" height="96" fill="#0D2C53"/>
    <rect y="0" width="48" height="10" fill="#FF5C1A"/>
    <rect y="12" width="48" height="2" fill="#F2F6FB" fill-opacity=".5"/>
    <g fill="#FF5C1A" fill-opacity=".85">
      <rect x="0" y="20" width="12" height="12"/><rect x="24" y="20" width="12" height="12"/>
      <rect x="12" y="32" width="12" height="12"/><rect x="36" y="32" width="12" height="12"/>
    </g>
    <rect y="48" width="48" height="2" fill="#F2F6FB" fill-opacity=".5"/>
    <rect y="54" width="48" height="6" fill="#1E4374"/>
    <g fill="#F2F6FB" fill-opacity=".35">
      <rect x="6" y="66" width="4" height="18"/><rect x="18" y="66" width="4" height="18"/>
      <rect x="30" y="66" width="4" height="18"/><rect x="42" y="66" width="4" height="18"/>
    </g>
    <rect y="88" width="48" height="4" fill="#FF8A5C" fill-opacity=".7"/>
  </svg>`);

// The same strip turned on its side for horizontal ribbons.
const KENTE_ACROSS = encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="96" height="48" viewBox="0 0 96 48"><g transform="translate(0 48) rotate(-90)">${decodeURIComponent(KENTE).replace(/^<svg[^>]*>/, '').replace(/<\/svg>\s*$/, '')}</g></svg>`);
const kenteStyle = (vertical: boolean): React.CSSProperties => vertical ? {
  backgroundImage: `url("data:image/svg+xml,${KENTE}")`,
  backgroundSize: '48px 96px'
} : {
  backgroundImage: `url("data:image/svg+xml,${KENTE_ACROSS}")`,
  backgroundSize: '48px 24px'
};

/** Adinkrahene, concentric circles: greatness and leadership. */
function Adinkrahene({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 100 100" className={className} fill="none" stroke="currentColor" strokeWidth="2.5" data-id="element-770" data-mp="37igwolz3qpit">
      <circle cx="50" cy="50" r="46" data-id="element-771" data-mp="3bkar4cipexaj" />
      <circle cx="50" cy="50" r="32" data-id="element-772" data-mp="3bh6i91uanddj" />
      <circle cx="50" cy="50" r="18" data-id="element-773" data-mp="zwfetvg7gev3" />
      <circle cx="50" cy="50" r="5" fill="currentColor" stroke="none" data-id="element-774" data-mp="zxh00sqxqhqj" />
    </svg>;
}
export function AfricanBackdrop() {
  const reduce = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll();
  const thread = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 30,
    mass: 0.4
  });

  // Gutter width is whatever sits outside the content column, less a clear gap
  // so nothing ever touches a heading or card edge.
  const gutter = `calc((100vw - ${CONTENT_WIDTH}px) / 2 - ${GUTTER_GAP}px)`;
  return <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-[5] hidden min-[1440px]:block" data-id="element-775" data-mp="1elljw01q4zaw">
      {(['left', 'right'] as const).map(side => <div key={side} className="absolute inset-y-0 flex justify-center" style={{
      [side]: 0,
      width: gutter
    }} data-id="element-776" data-mp="14tz2tn2pg608">
          {/* The woven strip, drifting one repeat at a time */}
          <div className="relative h-full w-3 overflow-hidden rounded-full opacity-70" data-id="element-777" data-mp="1xiwbcyh46ofs">
            <div className={`absolute inset-x-0 -top-24 -bottom-24 ${side === 'left' ? 'motion-safe:animate-kente-down' : 'motion-safe:animate-kente-up'}`} style={kenteStyle(true)} data-id="element-778" data-mp="1i0lkw9nms1rx" />
          </div>

          {side === 'left' && <motion.div className="absolute top-[38vh] h-16 w-16 text-gold opacity-40" animate={reduce ? undefined : {
        rotate: 360
      }} transition={reduce ? undefined : {
        duration: 90,
        ease: 'linear',
        repeat: Infinity
      }} data-id="element-779" data-mp="1d3cz7z39xflx">
              <Adinkrahene className="h-full w-full" data-id="element-780" data-mp="1r34jghkdvs8d" />
            </motion.div>}

          {side === 'right' && <svg className="absolute inset-y-0 left-1/2 h-full w-6 -translate-x-1/2" viewBox="0 0 24 1000" preserveAspectRatio="none" fill="none" data-id="element-781" data-mp="1w04mejlxsev9">
              <motion.path d={STITCH} stroke="#FF5C1A" strokeOpacity="0.6" strokeWidth="1.5" strokeLinejoin="round" vectorEffect="non-scaling-stroke" style={{
          pathLength: reduce ? 1 : thread
        }} data-id="element-782" data-mp="2ohqkcw6n7gy9" />
            </svg>}
        </div>)}
    </div>;
}

/** A slim woven ribbon for use as a section divider. It carries no text. */
export function KenteRibbon({
  className = ''
}: {
  className?: string;
}) {
  return <div aria-hidden="true" className={`relative h-3 w-full overflow-hidden ${className}`} data-id="element-783" data-mp="3qa2vu9s95fwu">
      <div className="absolute inset-y-0 -left-24 -right-24 motion-safe:animate-kente-across" style={kenteStyle(false)} data-id="element-784" data-mp="3lbi30vfbxyif" />
    </div>;
}

// A zigzag stitch from top to bottom of the gutter.
const STITCH = 'M12 0' + Array.from({
  length: 40
}, (_, i) => ` ${i % 2 === 0 ? 20 : 4} ${(i + 1) * 25}`).join('');